import {
  CardItemContent,
  CardItemIcon,
  CardSubtitle,
  CardTitle,
  StaticCardList,
  StaticCardListItem,
} from '@/components/Card'
import { SectionHeadline } from '@/components/SectionHeadline'
import { PageSection } from '@/features/jobs/details/components/PageLayout'
import type { BookkeepingViewModel } from '../../BookkeepingViewModel'
import { useId, type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import styles from './BookkeepingWorkItemsSection.module.css'
import { dateFormatters } from '@/utils/date-util'
import {
  AutoSkeleton,
  SkeletonList,
  useSkeletonListContext,
} from '@/components/Skeleton'

interface BookkeepingWorkItemsSectionContainerProps {
  vm: BookkeepingViewModel
}

/**
 * The BookkeepingWorkItemsSectionContainer
 */
export const BookkeepingWorkItemsSectionContainer = observer(
  ({ vm }: BookkeepingWorkItemsSectionContainerProps) => {
    const id = useId()
    return (
      <BookkeepingWorkItemsSection
        headlineId={id}
        headline="Finished items"
        testId="bookkeeping-work-items-section"
      >
        <BookkeepingWorkItemList aria-labelledby={id}>
          {vm.fetchBookkeepingProgressionInfo.pending ? (
            <SkeletonList count={2}>
              <BookkeepingWorkItemListItem
                isSkeleton
                title="Loading"
                subtitle="Please wait"
              />
            </SkeletonList>
          ) : (
            vm.bookkeepingProgressionInfo?.workItems.map((item) => (
              <BookkeepingWorkItemListItem
                key={item.workItemId}
                title={`${item.completedCount} of ${item.quantity} completed`}
                subtitle={item.inventoryItemName}
              />
            ))
          )}
        </BookkeepingWorkItemList>

        {vm.bookkeepingProgressionInfo?.completedAt &&
          !vm.fetchBookkeepingProgressionInfo.pending && (
            <BookkeepingWorkItemsFooter>
              Last updated{' '}
              {dateFormatters.dateTimeFull.format(
                vm.bookkeepingProgressionInfo.completedAt,
              )}
            </BookkeepingWorkItemsFooter>
          )}
      </BookkeepingWorkItemsSection>
    )
  },
)

interface BookkeepingWorkItemsFooterProps {
  children?: ReactNode
}

/**
 * The BookkeepingWorkItemsFooter
 */
export function BookkeepingWorkItemsFooter({
  children,
}: BookkeepingWorkItemsFooterProps) {
  return <div className={styles.footer}>{children}</div>
}

interface BookkeepingWorkItemsSectionProps {
  headlineId: string
  headline: string
  children?: ReactNode
  testId?: string
}

/**
 * The BookkeepingWorkItemsSection
 */
export function BookkeepingWorkItemsSection({
  headlineId,
  headline,
  children,
  testId,
}: BookkeepingWorkItemsSectionProps) {
  return (
    <PageSection testId={testId}>
      <SectionHeadline id={headlineId}>{headline}</SectionHeadline>
      {children}
    </PageSection>
  )
}

interface BookkeepingWorkItemListProps {
  'aria-label'?: string
  'aria-labelledby'?: string
  children?: ReactNode
  testId?: string
}

/**
 * The BookkeepingWorkItemList
 */
export function BookkeepingWorkItemList({
  children,
  ...rest
}: BookkeepingWorkItemListProps) {
  return <StaticCardList {...rest}>{children}</StaticCardList>
}

interface BookkeepingWorkItemListItemProps {
  title: string
  subtitle: string
  isSkeleton?: boolean
}

/**
 * The BookkeepingWorkItemListItem
 */
export function BookkeepingWorkItemListItem({
  title,
  subtitle,
  isSkeleton,
}: BookkeepingWorkItemListItemProps) {
  const { vary } = useSkeletonListContext()
  return (
    <StaticCardListItem>
      <CardItemContent
        icon={<CardItemIcon name="open-book" skeleton={isSkeleton} />}
      >
        <CardTitle>
          <AutoSkeleton characters={vary(20)} isSkeleton={isSkeleton}>
            {title}
          </AutoSkeleton>
        </CardTitle>
        <CardSubtitle variant="label">
          <AutoSkeleton characters={vary(80)} isSkeleton={isSkeleton}>
            {subtitle}
          </AutoSkeleton>
        </CardSubtitle>
      </CardItemContent>
    </StaticCardListItem>
  )
}
